import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';

import PerformanceRadial from '@/components/shared/PerformanceRadial';
import AstroImage from '@/components/ui/custom/AstroImage';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { Badge, Collapse, Divider } from '@/components/ui/daisy';

import {
  UnitOverviewCardBannerProps,
  UnitOverviewCardContentProps,
  UnitOverviewCardDescriptionProps,
  UnitOverviewCardHeaderProps,
  UnitOverviewCardLessonsHeaderProps,
  UnitOverviewCardNameProps,
  UnitOverviewCardProgressProps,
  UnitOverviewCardProps,
  UnitOverviewCardShortcutsProps,
  UnitOverviewCardSummaryProps,
} from './types';

const Root = ({ className, children, ...props }: UnitOverviewCardProps) => (
  <Collapse
    indicator="plus"
    className={twMerge('bg-base-200 rounded-2xl', className)}
    {...props}
  >
    {children}
  </Collapse>
);

const Header = ({ className, children }: UnitOverviewCardHeaderProps) => (
  <Collapse.Title
    className={twMerge(
      'min-h-19 gap-3 after:top-1/2 after:-translate-y-full',
      className,
    )}
  >
    {children}
  </Collapse.Title>
);

const useUnitCardTranslation = () =>
  useTranslation('translation', { keyPrefix: 'courseLessons.unitCard' });

const Progress = ({ progress, className }: UnitOverviewCardProgressProps) => {
  const { t } = useUnitCardTranslation();

  return (
    <PerformanceRadial
      performance={progress}
      label={t('progressLabel')}
      className={twMerge('shrink-0', className)}
    />
  );
};

const Name = ({ className, children }: UnitOverviewCardNameProps) => (
  <h2
    className={twMerge(
      'truncate text-base font-semibold text-base-content',
      className,
    )}
  >
    {children}
  </h2>
);

const Content = ({ className, children }: UnitOverviewCardContentProps) => (
  <Collapse.Content
    className={twMerge('flex flex-col gap-4 px-3 md:px-4', className)}
  >
    {children}
  </Collapse.Content>
);

const Summary = ({ className, children }: UnitOverviewCardSummaryProps) => (
  <div
    className={twMerge(
      'flex flex-col gap-4 md:flex-row md:items-center md:justify-between md:gap-2',
      className,
    )}
  >
    {children}
  </div>
);

const LessonsHeader = ({
  completed,
  total,
  className,
}: UnitOverviewCardLessonsHeaderProps) => {
  const { t } = useUnitCardTranslation();

  return (
    <div className={twMerge('flex items-center gap-4 py-4', className)}>
      <span className="text-sm text-base-content">{t('lessons')}</span>
      <Divider className="m-0 flex-1 mt-0.5" />
      <ConditionalRenderer condition={total !== undefined}>
        <Badge size="sm" variant="soft">
          <ConditionalRenderer
            condition={completed !== undefined}
            fallback={t('lessonsCount', { count: total })}
          >
            {t('completedCount', { completed, total })}
          </ConditionalRenderer>
        </Badge>
      </ConditionalRenderer>
    </div>
  );
};

const Banner = ({ src, className }: UnitOverviewCardBannerProps) => {
  if (!src) return null;

  return (
    <AstroImage
      src={src}
      alt=""
      className={twMerge(
        'h-30 w-full shrink-0 rounded-2xl object-cover md:w-64',
        className,
      )}
    />
  );
};

const Description = ({
  className,
  children,
}: UnitOverviewCardDescriptionProps) => (
  <span
    className={twMerge(
      'text-xs leading-5 text-base-content md:min-w-0 md:flex-1 md:self-start',
      className,
    )}
  >
    <p className="md:max-w-3/4">{children}</p>
  </span>
);

const Shortcuts = ({ className, children }: UnitOverviewCardShortcutsProps) => (
  <ul className={twMerge('flex gap-2 md:ml-auto md:shrink-0', className)}>
    {children}
  </ul>
);

Root.displayName = 'UnitOverviewCard';
Header.displayName = 'UnitOverviewCard.Header';
Progress.displayName = 'UnitOverviewCard.Progress';
Name.displayName = 'UnitOverviewCard.Name';
Content.displayName = 'UnitOverviewCard.Content';
Summary.displayName = 'UnitOverviewCard.Summary';
LessonsHeader.displayName = 'UnitOverviewCard.LessonsHeader';
Banner.displayName = 'UnitOverviewCard.Banner';
Description.displayName = 'UnitOverviewCard.Description';
Shortcuts.displayName = 'UnitOverviewCard.Shortcuts';

export const UnitOverviewCard = Object.assign(Root, {
  Header,
  Progress,
  Name,
  Content,
  Summary,
  LessonsHeader,
  Banner,
  Description,
  Shortcuts,
});
